'use client';
import React, { useState } from 'react';
import Link from '@/core/i18n/navigation';
import { toast } from '@/core/utils/toast';
import { Button } from '@/core/components/ui/button';
import { cn } from '@/core/utils/cn';
import { formatStyle, getHoverClass } from '../blockHelpers';
import { BlockProps } from './types';
import { trackClick } from '@/core/utils/analytics';
import { useTranslation } from '@/core/components/providers/TranslationProvider';

function BuilderButton({
  block,
  styles,
  onClick,
  isAdmin,
}: {
  block: any;
  styles: any;
  onClick?: any;
  isAdmin: boolean;
}) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  const currentStyles = isHovered
    ? {
        ...styles,
        backgroundColor: styles.hoverBackgroundColor || styles.backgroundColor,
        color: styles.hoverColor || styles.color,
        borderColor: styles.hoverBorderColor || styles.borderColor,
      }
    : styles;

  const sizeMultiplier = styles.sizeMultiplier || 1;
  const baseFontSize = parseInt(styles.fontSize) || 16;
  const finalFontSize = `${baseFontSize * sizeMultiplier}px`;

  const subtext: string | undefined = block.settings?.subtext;
  const subtextStyle: React.CSSProperties = {
    color: styles.subtextColor || currentStyles.color || '#ffffff',
    fontSize: styles.subtextFontSize ? `${styles.subtextFontSize * sizeMultiplier}px` : `${baseFontSize * 0.7 * sizeMultiplier}px`,
    fontWeight: styles.subtextFontWeight || 'normal',
    fontFamily: styles.subtextFontFamily && styles.subtextFontFamily !== 'inherit' ? styles.subtextFontFamily : undefined,
    opacity: styles.subtextColor ? 1 : 0.8,
    lineHeight: 1.2,
  };

  // The "Animacje i Efekty" panel's hover effect (scale/lift/tilt/glow) is applied
  // right here, on the actual <Button>, instead of on the block's outer wrapper —
  // that wrapper includes this block's own padding/margin, so hovering that empty
  // space used to grow/tilt the whole section instead of just the button itself
  // (see BlockRenderer.tsx / blockHelpers.ts HOVER_SCOPED_TO_INNER_ELEMENT).
  const hoverVars: React.CSSProperties = {
    '--hover-scale': styles.hoverScaleValue || undefined,
    '--hover-lift': styles.hoverLiftDistance ? `${styles.hoverLiftDistance}px` : undefined,
    '--hover-tilt': styles.hoverTiltAngle ? `${styles.hoverTiltAngle}deg` : undefined,
    '--hover-glow-color': styles.hoverGlowColor || undefined,
    '--hover-glow-blur': styles.hoverGlowBlur ? `${styles.hoverGlowBlur}px` : undefined,
    '--hover-duration': styles.hoverTransitionDuration || undefined,
  } as any;

  // Internal padding (top/bottom, left/right independently) — separate fields so a
  // merchant can e.g. widen the button without also stretching its height, same
  // pattern as "Do koszyka"'s btnPaddingY/btnPaddingX.
  const hasCustomPadding = styles.btnPaddingY !== undefined || styles.btnPaddingX !== undefined;
  const padding = hasCustomPadding
    ? `${(styles.btnPaddingY ?? 12) * sizeMultiplier}px ${(styles.btnPaddingX ?? 32) * sizeMultiplier}px`
    : undefined;

  // Text alignment *inside* the button — independent from the block-level alignment
  // (which positions the whole button within its section, see ButtonBlock below).
  // Only matters once the button is wider than its text (cover/custom width), but a
  // merchant may still want the text pinned left/right with breathing room instead
  // of dead-centered in all that extra space.
  const contentAlign: 'left' | 'center' | 'right' = styles.contentAlign || 'center';
  const contentAlignClass = contentAlign === 'left' ? 'items-start text-left' : contentAlign === 'right' ? 'items-end text-right' : 'items-center text-center';

  return (
    <Button
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...hoverVars,
        borderRadius: formatStyle(styles.borderRadius),
        backgroundColor: currentStyles.backgroundColor || '#2563eb',
        color: currentStyles.color || '#ffffff',
        fontSize: finalFontSize,
        fontWeight: styles.fontWeight || 'bold',
        fontFamily: styles.fontFamily && styles.fontFamily !== 'inherit' ? styles.fontFamily : undefined,
        borderWidth: formatStyle(styles.borderWidth),
        borderColor: currentStyles.borderColor,
        borderStyle: styles.borderWidth ? 'solid' : undefined,
        padding,
        width: styles.cover ? '100%' : styles.width ? `${styles.width}%` : undefined,
        boxShadow: styles.shadowEnabled
          ? `${styles.shadowOffsetX || 0}px ${styles.shadowOffsetY || 4}px ${styles.shadowBlur || 15}px ${styles.shadowSpread || 0}px ${styles.shadowColor || 'rgba(0,0,0,0.2)'}`
          : undefined,
      }}
      className={cn(
        'cursor-pointer flex flex-col gap-0.5',
        contentAlignClass,
        getHoverClass(styles.hoverEffect),
        subtext ? 'leading-tight' : 'leading-none',
        !hasCustomPadding && (subtext ? 'px-8 py-2 h-auto min-h-12' : 'px-8 h-12'),
        styles.shadowEnabled === undefined && !styles.cover && 'shadow-lg'
      )}
      onClick={onClick}
    >
      <span>{block.content || t('button_block.label_default', 'Przycisk')}</span>
      {subtext && <span style={subtextStyle}>{subtext}</span>}
    </Button>
  );
}

export function ButtonBlock({ block, blockStyles, currentStyles, isAdmin }: BlockProps) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        'flex w-full',
        blockStyles.textAlign === 'center'
          ? 'justify-center'
          : blockStyles.textAlign === 'right'
            ? 'justify-end'
            : 'justify-start'
      )}
    >
      {(() => {
        const actionType = block.settings?.actionType || 'link';
        let href = block.settings?.href || '#';
        let target = block.settings?.target || '_self';
        let rel = target === '_blank' ? 'noopener noreferrer' : undefined;
        let onClick: ((e: React.MouseEvent) => void) | undefined = undefined;

        if (actionType === 'scroll') {
          const targetId = (block.settings?.scrollId || '').replace(/^#/, '');
          href = `#${targetId}`;
          onClick = (e: React.MouseEvent) => {
            if (isAdmin) {
              e.preventDefault();
              return;
            }
            if (targetId) {
              const el = document.getElementById(targetId);
              if (el) {
                e.preventDefault();
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                window.history.replaceState(null, '', `#${targetId}`);
              }
            }
          };
        } else if (actionType === 'phone') {
          href = `tel:${block.settings?.phone || ''}`;
          if (!isAdmin) onClick = () => trackClick('tel', block.settings?.phone || '');
        } else if (actionType === 'email') {
          href = `mailto:${block.settings?.email || ''}?subject=${encodeURIComponent(block.settings?.emailSubject || '')}`;
          if (!isAdmin) onClick = () => trackClick('mailto', block.settings?.email || '');
        } else if (actionType === 'download') {
          href = block.settings?.fileUrl || '#';
          target = '_blank';
        } else if (actionType === 'cart' || actionType === 'modal') {
          href = '#';
          onClick = (e: React.MouseEvent) => {
            e.preventDefault();
            if (actionType === 'cart') {
              toast.success(t('button_block.preview_add_to_cart', 'Przycisk: Dodano produkt do koszyka (Podgląd)'));
            } else if (actionType === 'modal') {
              toast.info(t('button_block.preview_open_modal', 'Przycisk: Otwieranie Pop-upa (ID: {id})').replace('{id}', String(block.settings?.modalId)));
            }
          };
        }

        if (actionType === 'link' && !onClick && !isAdmin && typeof window !== 'undefined' && /^https?:\/\//.test(href) && !href.startsWith(window.location.origin)) {
          onClick = () => trackClick('link', href);
        }

        const isNavigable = ['link', 'scroll', 'phone', 'email', 'download'].includes(actionType);

        return (
          <div
            className={cn(
              'flex w-full',
              blockStyles.textAlign === 'center'
                ? 'justify-center'
                : blockStyles.textAlign === 'right'
                  ? 'justify-end'
                  : 'justify-start'
            )}
          >
            {isNavigable ? (
              <Link
                href={href}
                target={target}
                rel={rel}
                className="contents"
                // In the builder canvas, clicking should select the block, not navigate away.
                onClick={onClick ? onClick : (isAdmin ? (e) => e.preventDefault() : undefined)}
              >
                <BuilderButton block={block} styles={currentStyles} isAdmin={isAdmin} />
              </Link>
            ) : (
              <BuilderButton
                block={block}
                styles={currentStyles}
                onClick={onClick}
                isAdmin={isAdmin}
              />
            )}
          </div>
        );
      })()}
    </div>
  );
}
